import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Building2, ChevronDown, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCompany, Company } from '../contexts/CompanyContext';

/**
 * Company switcher for multi-company logins. Lists the companies this login was granted
 * (from the auth payload's machineCompanies) and, on selection, scopes the Machines page
 * to that company. The default selection is handled in CompanyContext; this only lets the
 * user switch. Hidden for super-admins and single-tenant logins, and off the Machines page.
 */
const MachineCompanySwitcher: React.FC = () => {
  const { user } = useAuth();
  const { selectedCompany, setSelectedCompany } = useCompany();
  const queryClient = useQueryClient();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const isMultiCompany =
    !!user && user.role !== 'SUPERADMIN' && Array.isArray(user.machineTenantIds) && user.machineTenantIds.length > 0;

  const companies = useMemo<Company[]>(() => {
    const list: Company[] = (user?.machineCompanies as Company[]) || [];
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [user]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // The switcher only affects the Machines grid, so only show it there.
  if (!isMultiCompany || location.pathname !== '/machines') return null;

  const choose = (company: Company) => {
    setOpen(false);
    if (company.tenantId === selectedCompany?.tenantId) return;
    setSelectedCompany(company);
    // Machine data was cached for the previous company — refetch through the new scope.
    queryClient.invalidateQueries();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Select company"
        className="flex items-center gap-2 h-11 px-3 rounded-xl border bg-[#F2F2F7] border-[#E8EBF1] text-[#1B202A] hover:bg-[#EAEBF0] transition-colors"
      >
        <Building2 className="h-5 w-5 flex-shrink-0 text-[#0055a9]" />
        <span className="text-sm font-semibold truncate max-w-[9rem] hidden sm:block">
          {selectedCompany?.name || 'Select company'}
        </span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="fixed left-2 right-2 top-[80px] sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-[240px] bg-white rounded-xl shadow-[0_7px_29px_rgba(100,100,111,0.2)] border border-[#E1E6EF] py-1 z-[1002] max-h-[70vh] overflow-y-auto">
          <div className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
            Companies
          </div>
          {companies.map((c) => {
            const active = selectedCompany?.tenantId === c.tenantId;
            return (
              <button
                key={c.tenantId}
                onClick={() => choose(c)}
                className={`flex items-center justify-between w-full px-4 py-2.5 text-sm text-left hover:bg-gray-50 transition-colors ${
                  active ? 'text-[#0055a9] font-semibold' : 'text-[#0E131B]'
                }`}
              >
                <span className="truncate">{c.name}</span>
                {active && <Check className="h-4 w-4 flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MachineCompanySwitcher;
